import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import DailyPuzzleList from './page/DailyPuzzleList';
import PuzzleGame from './page/PuzzleGame';
import HowToPlayModal from './info/HowToPlayModal';
import { theme, commonStyles } from '../theme';

const GlobalStyle = createGlobalStyle`
  ${theme}
  
  body {
    font-family: Arial, sans-serif;
    margin: 0;
    padding: 0;
    background-color: ${commonStyles.primaryBackground};
    color: ${commonStyles.primaryText};
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  h1, h2, h3 {
    color: ${commonStyles.primaryText};
  }
`;

const AppContainer = styled.div`
  padding: 20px;
  padding-top: 60px; /* Account for fixed header */
  
  @media (max-width: 768px) {
    padding: 8px;
    padding-top: 70px; /* Account for fixed header on mobile */
  }
`;

const AppHeader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: ${commonStyles.primaryBackground};
  z-index: 1000;
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

const ClickableLogo = styled.img`
  height: 40px;
  cursor: pointer;

  @media (max-width: 768px) {
    height: 35px;
  }
`;

const HowToPlayIcon = styled.button`
  background: none;
  border: none;
  color: var(--color-text, ${commonStyles.primaryText});
  cursor: pointer;
  font-size: 1.2rem;
  padding: 8px;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--color-hover-bg, rgba(0, 0, 0, 0.1));
  }

  &:focus {
    outline: 2px solid var(--color-focus, ${commonStyles.inputBorder});
    outline-offset: 1px;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Button = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 15px;
  cursor: pointer;
  border-radius: 5px;

  &:hover {
    background-color: #0056b3;
  }
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
  box-sizing: border-box;
`;

const ErrorText = styled.div`
  color: red;
  font-size: 0.9em;
`;

const SuccessText = styled.div`
  color: green;
  font-size: 0.9em;
`;

const WordComplete = styled.span`
  color: green;
`;

const AppRoutes = () => {
  const navigate = useNavigate();
  const [showHowToPlayModal, setShowHowToPlayModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Detect theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
  
  const onSelectPuzzleByDate = (puzzleDate) => {
    navigate(`/daily/${puzzleDate}`);
  };

  return (
    <AppContainer>
      <AppHeader>
        <ClickableLogo 
          src={isDarkMode ? '/chainlink_light.svg' : '/chainlink_dark.svg'}
          alt="Chainlink"
          onClick={() => navigate('/')}
        />
        <HowToPlayIcon onClick={() => setShowHowToPlayModal(true)} title="How to Play">
          ℹ️
        </HowToPlayIcon>
      </AppHeader>
      
      <HowToPlayModal
        isOpen={showHowToPlayModal}
        onClose={() => setShowHowToPlayModal(false)}
      />
      
      <Routes>
        <Route path="/" element={<DailyPuzzleList onSelectPuzzle={onSelectPuzzleByDate} />} />
        <Route path="/game/:puzzleKeyParam" element={<PuzzleGame />} />
        <Route path="/daily/:dateParam" element={<PuzzleGame />} />
      </Routes>
    </AppContainer>
  );
};

const App = () => {
  // App icon color selection
  useEffect(() => {
    const updateFavicon = (isDark) => {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.type = 'image/svg+xml';
      link.rel = 'icon';
      link.href = isDark ? '/chainlink_light.svg' : '/chainlink_dark.svg';
      document.head.appendChild(link);
    };

    // Check initial theme
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    updateFavicon(mediaQuery.matches);

    // Listen for changes
    const handleChange = (e) => updateFavicon(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <>
      <GlobalStyle />
      <Router>
        <AppRoutes />
      </Router>
    </>
  );
};

export default App;
export { Container, Button, Input, ErrorText, SuccessText, WordComplete };